// /client/src/pages/Admin/DashboardAdherents/StatisticsCards.jsx
import React, { useEffect, useState } from "react";
import {
  FaUserFriends,
  FaRegListAlt,
  FaUsers,
  FaChartLine,
} from "react-icons/fa";
import { RiNumbersFill } from "react-icons/ri";
import {
  comptageStatutLicencie,
  comptageInscriptionsTotales,
} from "../../../services/dashboardServices";
import { countUsers } from "../../../services/userServices";
import { countVisites } from "../../../services/visitesServices";
import VisitesLineChart from "./VisitesLineChart";

/* Modale générique */
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[60]">
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div className="absolute inset-0 p-4 sm:p-6 flex items-center justify-center">
        <div className="w-full max-w-6xl rounded-2xl overflow-hidden shadow-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white dark:bg-slate-900">
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-white text-lg sm:text-xl font-bold">
                {title}
              </h3>
              <button
                onClick={onClose}
                className="text-white/90 hover:text-white text-xl leading-none px-2"
                aria-label="Fermer"
              >
                ×
              </button>
            </div>
          </div>
          <div className="p-5 sm:p-6">{children}</div>
        </div>
      </div>
    </div>
  );
};

/* Petite carte stat réutilisable (même langage visuel que le reste) */
function StatCard({ icon, label, value, gradient, action }) {
  return (
    <div className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur p-4 sm:p-5 shadow-sm hover:shadow transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className={`h-12 w-12 rounded-xl text-white grid place-items-center text-xl bg-gradient-to-br ${gradient}`}
          >
            {icon}
          </div>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {label}
            </div>
            <div className="text-2xl font-bold text-slate-900 dark:text-white">
              {value}
            </div>
          </div>
        </div>

        {action ? <div className="shrink-0 -mt-1">{action}</div> : null}
      </div>
    </div>
  );
}

/* Skeleton loading pour les cartes */
function StatCardSkeleton() {
  return (
    <div className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur p-4 sm:p-5">
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-xl bg-slate-200 dark:bg-slate-800 animate-pulse" />
        <div className="flex-1">
          <div className="h-3 w-28 rounded bg-slate-200 dark:bg-slate-800 animate-pulse mb-2" />
          <div className="h-7 w-20 rounded bg-slate-200 dark:bg-slate-800 animate-pulse" />
        </div>
      </div>
    </div>
  );
}

export default function StatisticsCards() {
  const [counts, setCounts] = useState({
    adherents: 0,
    inscrits: 0,
    users: 0,
    visites: 0,
  });
  const [loading, setLoading] = useState(true);
  const [openVisites, setOpenVisites] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [
          adherentsResponse,
          inscritsResponse,
          usersResponse,
          visitesResponse,
        ] = await Promise.all([
          comptageStatutLicencie(), // { nombreAdherents }
          comptageInscriptionsTotales(), // { nombreInscrits }
          countUsers(), // { count }
          countVisites(), // { count }
        ]);

        if (!mounted) return;
        setCounts({
          adherents: Number(adherentsResponse?.nombreAdherents || 0),
          inscrits: Number(inscritsResponse?.nombreInscrits || 0),
          users: Number(usersResponse?.count || 0),
          visites: Number(visitesResponse?.count || 0),
        });
      } catch (e) {
        console.error("Erreur lors de la récupération des statistiques", e);
      } finally {
        mounted && setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const n = (x) => new Intl.NumberFormat("fr-FR").format(x);

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {loading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <StatCard
              icon={<FaUserFriends />}
              label="Adhérents licenciés"
              value={n(counts.adherents)}
              gradient="from-emerald-500 to-teal-500"
            />
            <StatCard
              icon={<FaRegListAlt />}
              label="Inscrits & cours d’essai"
              value={n(counts.inscrits)}
              gradient="from-amber-500 to-orange-500"
            />
            <StatCard
              icon={<FaUsers />}
              label="Utilisateurs"
              value={n(counts.users)}
              gradient="from-indigo-500 to-violet-500"
            />
            <StatCard
              icon={<RiNumbersFill />}
              label="Visites sur site"
              value={n(counts.visites)}
              gradient="from-slate-700 to-slate-900"
              action={
                <button
                  type="button"
                  onClick={() => setOpenVisites(true)}
                  title="Ouvrir le graphique des visites"
                  aria-label="Ouvrir le graphique des visites"
                  className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-white/90 dark:bg-slate-800/80 text-slate-700 dark:text-slate-200 ring-1 ring-slate-200/70 dark:ring-slate-700/70 hover:bg-white dark:hover:bg-slate-800 hover:shadow-sm transition"
                >
                  <FaChartLine className="text-sm" />
                </button>
              }
            />
          </>
        )}
      </div>

      {/* Modale graphique visites */}
      <Modal
        isOpen={openVisites}
        onClose={() => setOpenVisites(false)}
        title="Visites — évolution"
      >
        <div className="w-full max-w-5xl mx-auto">
          <VisitesLineChart />
        </div>
      </Modal>
    </>
  );
}
